import React, { useState, useEffect, useRef } from "react";
import generateEstimatePDF from "./EstimatePDF";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "./services/api";

const EditEstimate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- STATE ---
  const [customer, setCustomer] = useState({
    name: "",
    city: "",
    mobile: "",
    date: new Date().toISOString().split("T")[0],
  });

  const [jobDetails, setJobDetails] = useState({
    jobCardNo: "",
    hp: "",
    type: "V4",
  });

  const [items, setItems] = useState([
    { name: "", price: "", quantity: "", description: "" },
  ]);

  const [description, setDescription] = useState("");
  const [includeGst, setIncludeGst] = useState(false);

  // Price Check / Autocomplete State
  const [suggestions, setSuggestions] = useState([]);
  const [activeRow, setActiveRow] = useState(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const debounceRef = useRef(null);

  // Discount Logic
  const [discountValue, setDiscountValue] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Success Modal State
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [estimateNo, setEstimateNo] = useState("");

  // Fetch Existing Estimate
  useEffect(() => {
    const fetchEstimate = async () => {
        try {
            const res = await api.get(`/estimates/${id}`);
            const data = res.data;

            if (data) {
                setCustomer({
                    name: data.customer_name || "",
                    city: data.customer_city || "",
                    mobile: data.customer_mobile || "",
                    date: data.estimate_date ? data.estimate_date.split('T')[0] : new Date().toISOString().split("T")[0],
                });

                setJobDetails({
                    jobCardNo: data.job_card_no || "",
                    hp: data.motor_hp || "",
                    type: data.motor_type || "V4",
                });

                setItems(data.items || []);
                setDescription(data.description || "");
                setDiscountValue(data.discount_amount || "");
                setEstimateNo(data.estimate_no || "");
                setIncludeGst(data.include_gst !== undefined && data.include_gst !== null ? !!data.include_gst : false);
            }
        } catch (err) {
            console.error("Error fetching estimate:", err);
            alert("Failed to load estimate.");
        } finally {
            setIsLoading(false);
        }
    };

    fetchEstimate();
  }, [id]);


  // Auto-scroll when active suggestion changes
  useEffect(() => {
    if (activeSuggestionIndex >= 0) {
      const el = document.getElementById(`suggestion-${activeSuggestionIndex}`);
      if (el) {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeSuggestionIndex]);

  // --- HANDLERS ---
  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobile") {
      setCustomer((prev) => ({ ...prev, [name]: value.replace(/[^0-9]/g, "") }));
    } else {
      setCustomer((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleJobChange = (e) => {
    const { name, value } = e.target;
    setJobDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const newItems = [...prev];
      if (field === "price" || field === "quantity") {
          // Allow only numbers
          newItems[index][field] = value.replace(/[^0-9.]/g, "");
      } else {
          newItems[index][field] = value;
      }
      return newItems;
    });

    // If typing logic for name
    if (field === "name") {
        if (value.length < 2) {
            setSuggestions([]);
            setActiveRow(null);
            setActiveSuggestionIndex(-1);
            return;
        }
        
        setActiveRow(index);
        
        // Debounce API call
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
             try {
                const res = await api.get(`/quotations/suggest-item?query=${encodeURIComponent(value)}`);
                setSuggestions(res.data || []);
                setActiveSuggestionIndex(-1); // Reset selection on new data
             } catch (err) {
                 console.error("Suggestion error", err);
             }
        }, 300);
    }
  };

  const handleKeyDown = (e, index) => {
    if (activeRow !== index || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
        selectSuggestion(index, suggestions[activeSuggestionIndex]);
      }
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setActiveRow(null);
    }
  };

  const selectSuggestion = async (index, itemName) => {
      // 1. Update Name
      handleItemChange(index, "name", itemName);
      // 2. Set Default Quantity to 1
      handleItemChange(index, "quantity", "1");
      
      setSuggestions([]);
      setActiveRow(null);
      setActiveSuggestionIndex(-1);

      // 3. Fetch Price
      try {
          const res = await api.get(`/quotations/product-rate?item=${encodeURIComponent(itemName)}`);
          if (res.data && res.data.length > 0) {
              const rate = res.data[0].rate; // taking first match
              handleItemChange(index, "price", String(rate));
          }
      } catch (err) {
          console.error("Price fetch error", err);
      }
  };

  const addItem = () => {
    setItems((prev) => [...prev, { name: "", price: "", quantity: "", description: "" }]);
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    setActiveRow(null); // Clear if removing active row
  };

  // --- CALCULATIONS ---
  const total = items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.price) || 0;
    return sum + (qty * price);
  }, 0);

  const discountAmount = parseFloat(discountValue) || 0;
  const netTotal = Math.max(0, total - discountAmount);

  const isFormValid = customer.name && customer.mobile && customer.city;

  const handleDownload = () => {
      if (!isFormValid) return;
      generateEstimatePDF({ customer, jobDetails, items, total, discountAmount, description, includeGst });
  };
  
  const rupee = (val) => val.toLocaleString("en-IN", { maximumFractionDigits: 2 });


  const handleUpdate = async () => {
    if (!isFormValid || isSaving) return;
    setIsSaving(true);
    try {
        await api.put(`/estimates/${id}`, { 
            customer, 
            jobDetails, 
            items, 
            total: netTotal, 
            discountAmount,
            description,
            includeGst
        });
        
        setShowSuccessModal(true);
    } catch (err) {
        console.error("Update error", err);
        alert("Failed to update estimate.");
    } finally {
        setIsSaving(false);
    }
  };

  if (isLoading) {
      return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white">
      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="orb absolute w-96 h-96 top-20 left-20 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur-3xl opacity-20"></div>
        <div className="orb absolute w-80 h-80 bottom-10 right-10 bg-gradient-to-r from-indigo-600 to-cyan-600 rounded-full blur-3xl opacity-20"></div>
      </div>


      <div className="container mx-auto px-4 md:px-14 py-8 md:py-16 max-w-5xl relative z-10">
        
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400">
            Edit Estimate
          </h1>
          <p className="text-purple-200 mt-2">Update repair estimate details.</p>
        </div>

        <div className="glass rounded-3xl p-6 md:p-10 shadow-2xl border border-purple-500/20">
          
          {/* 1. Customer Details */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-purple-300 mb-4 border-b border-white/10 pb-2">
              <i className="fas fa-user-circle mr-2"></i> Customer Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                name="name"
                placeholder="Customer Name"
                value={customer.name}
                onChange={handleCustomerChange}
                className="input-glow bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-purple-300 focus:outline-none"
              />
              <input
                name="city"
                placeholder="City"
                value={customer.city}
                onChange={handleCustomerChange}
                className="input-glow bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-purple-300 focus:outline-none"
              />
              <input
                name="mobile"
                placeholder="Mobile Number"
                value={customer.mobile}
                onChange={handleCustomerChange}
                className="input-glow bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-purple-300 focus:outline-none"
              />
              <input
                name="date"
                type="date"
                value={customer.date}
                onChange={handleCustomerChange}
                className="input-glow bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-purple-300 focus:outline-none"
              />
            </div>
          </div>

          {/* 2. Job / Motor Details */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-purple-300 mb-4 border-b border-white/10 pb-2">
              <i className="fas fa-cogs mr-2"></i> Job & Motor Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
               <input
                name="jobCardNo"
                placeholder="Job Card No"
                value={jobDetails.jobCardNo}
                onChange={handleJobChange}
                className="input-glow bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-purple-300 focus:outline-none"
              />
               
               {/* TYPE DROPDOWN */}
               <div className="relative">
                 <select
                  name="type"
                  value={jobDetails.type}
                  onChange={handleJobChange}
                  className="w-full appearance-none input-glow bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-purple-300 focus:outline-none"
                >
                  <option value="V4" className="bg-slate-900 text-purple-200">V4</option>
                  <option value="V6" className="bg-slate-900 text-purple-200">V6</option>
                  <option value="Openwell" className="bg-slate-900 text-purple-200">Openwell</option>
                  <option value="Induction" className="bg-slate-900 text-purple-200">Induction</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-purple-300">
                  <i className="fas fa-chevron-down text-xs"></i>
                </div>
               </div>

               <input
                name="hp"
                placeholder="HP / Capacity"
                value={jobDetails.hp}
                onChange={handleJobChange}
                className="input-glow bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-purple-300 focus:outline-none"
              />
            </div>
          </div>

          {/* 3. Items Table */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4 border-b border-white/10 pb-2">
                <h3 className="text-xl font-bold text-purple-300">
                    <i className="fas fa-list mr-2"></i> Repair Consumables & Labor
                </h3>
            </div>
            
            <div className="space-y-3">
                {items.length === 0 && (
                    <div className="text-center py-4 text-white/50 italic">No items added. Click "Add Item" to start.</div>
                )}
                {items.map((item, index) => (
                    <div key={index} className="flex flex-col md:flex-row gap-2 bg-white/5 p-3 rounded-xl items-center border border-white/5 relative">
                        <span className="text-white/50 w-6 text-center">{index + 1}.</span>
                        
                        {/* Autocomplete Container */}
                        <div className="flex-1 relative flex flex-col gap-1">
                            <input
                                placeholder="Description (e.g. Rewinding Wire)"
                                value={item.name}
                                onChange={(e) => handleItemChange(index, "name", e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                className="w-full bg-transparent border-b border-white/20 px-2 py-1 text-white placeholder-white/30 focus:outline-none focus:border-purple-400"
                            />
                            <input
                                placeholder="Add description (optional)..."
                                value={item.description || ""}
                                onChange={(e) => handleItemChange(index, "description", e.target.value)}
                                className="w-full bg-transparent border-b border-white/20 px-2 py-1 text-sm text-white/70 placeholder-white/30 focus:outline-none focus:border-purple-400"
                            />
                            {activeRow === index && suggestions.length > 0 && (
                                <div className="absolute top-full left-0 right-0 bg-slate-900 border border-white/20 rounded-b-xl z-50 max-h-48 overflow-y-auto shadow-xl">
                                    {suggestions.map((s, idx) => (
                                        <div 
                                            key={idx}
                                            id={`suggestion-${idx}`}
                                            onClick={() => selectSuggestion(index, s)}
                                            className={`px-4 py-2 cursor-pointer text-sm ${
                                                idx === activeSuggestionIndex 
                                                ? "bg-purple-600 text-white" 
                                                : "text-purple-100 hover:bg-purple-600/20"
                                            }`}
                                        >
                                            {s}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="flex gap-2 w-full md:w-auto">
                            <input
                                placeholder="Qty"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                onFocus={() => setActiveRow(null)}
                                className="w-16 bg-transparent border-b border-white/20 px-2 py-1 text-white placeholder-white/30 text-center focus:outline-none focus:border-purple-400"
                            />
                            <input
                                placeholder="Rate (Incl GST)"
                                value={item.price}
                                onChange={(e) => handleItemChange(index, "price", e.target.value)}
                                className="w-24 bg-transparent border-b border-white/20 px-2 py-1 text-white placeholder-white/30 text-right focus:outline-none focus:border-purple-400"
                            />
                        </div>
                        
                        <div className="w-24 text-right font-mono text-emerald-400">
                            ₹{rupee(((includeGst ? (parseFloat(item.price)||0) / 1.18 : (parseFloat(item.price)||0))) * (parseFloat(item.quantity)||0))}
                        </div>
                        
                        <button 
                            onClick={() => removeItem(index)}
                            className="text-red-400 hover:text-red-300 px-2"
                        >
                            <i className="fas fa-trash"></i>
                        </button>
                    </div>
                ))}

                <button 
                    onClick={addItem}
                    className="w-full py-3 rounded-xl border-2 border-dashed border-purple-500/30 text-purple-300 hover:bg-purple-500/10 hover:border-purple-500/50 transition-all font-semibold"
                >
                    + Add An Item
                </button>
            </div>

            {/* Total Row */}
            <div className="flex justify-end mb-4 pr-2">
                <label className="flex items-center cursor-pointer gap-3 bg-slate-900/50 px-4 py-2 rounded-xl border border-white/10 hover:bg-slate-900/80 transition-colors">
                <span className="text-sm font-bold text-purple-300 uppercase tracking-widest">
                    Bill With GST
                </span>
                <div className="relative">
                    <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={includeGst}
                    onChange={(e) => setIncludeGst(e.target.checked)}
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${includeGst ? 'bg-emerald-500' : 'bg-slate-700'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${includeGst ? 'transform translate-x-4' : ''}`}></div>
                </div>
                </label>
            </div>
            
            <div className="mt-8 flex flex-col md:flex-row justify-end items-center gap-6 border-t border-white/10 pt-6">
                
                {/* Discount Controls */}
                <div className="flex flex-col items-center gap-2">
                     <span className="text-sm font-bold text-purple-300">Discount (₹)</span>
                     <input
                        type="number"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        placeholder="₹0"
                        className="w-24 bg-transparent border-b border-purple-500/50 py-1 text-lg font-bold text-center text-white focus:outline-none focus:border-purple-400 placeholder-purple-400/30"
                     />
                </div>

                <div className="text-right">
                    {includeGst ? (
                        <div className="flex flex-col items-end gap-1 mb-3 text-sm text-purple-200">
                             <div className="flex justify-between w-52">
                                 <span>Taxable Amount:</span>
                                 <span className="font-mono text-white">₹{rupee(netTotal / 1.18)}</span>
                             </div>
                             <div className="flex justify-between w-52">
                                 <span>CGST (9%):</span>
                                 <span className="font-mono text-white">₹{rupee((netTotal - (netTotal / 1.18)) / 2)}</span>
                             </div>
                             <div className="flex justify-between w-52 border-b border-white/10 pb-1 mb-1">
                                 <span>SGST (9%):</span>
                                 <span className="font-mono text-white">₹{rupee((netTotal - (netTotal / 1.18)) / 2)}</span>
                             </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-end gap-1 mb-3 text-sm text-purple-200">
                             <div className="flex justify-between w-52">
                                 <span>Total Amount:</span>
                                 <span className="font-mono text-white">₹{rupee(total)}</span>
                             </div>
                             {discountAmount > 0 && (
                                 <div className="flex justify-between w-52 text-red-400">
                                     <span>Discount:</span>
                                     <span className="font-mono">- ₹{rupee(discountAmount)}</span>
                                 </div>
                             )}
                        </div>
                    )}
                    <div className="text-3xl font-bold text-emerald-400">
                       ₹{rupee(netTotal)}
                    </div>
                </div>
            </div>
          </div>

          {/* 4. Description / Notes */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-purple-300 mb-4 border-b border-white/10 pb-2">
              <i className="fas fa-sticky-note mr-2"></i> Notes / Description
            </h3>
            <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter any additional notes, terms or technical description here..."
                className="w-full h-24 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-purple-300 focus:outline-none resize-none"
            ></textarea>
          </div>

          {/* Actions */}
          <div className="flex justify-center gap-6">
             <button
                onClick={handleUpdate}
                disabled={!isFormValid || isSaving}
                className={`px-8 py-4 rounded-full font-bold text-lg shadow-2xl flex items-center gap-3 transition-all ${
                    isFormValid && !isSaving
                    ? "bg-gradient-to-r from-emerald-500 to-teal-500 hover:scale-105 hover:from-emerald-400 hover:to-teal-400 text-white cursor-pointer" 
                    : "bg-gray-600 text-gray-400 opacity-50 cursor-not-allowed"
                }`}
             >
                {isSaving ? (
                     <><i className="fas fa-spinner fa-spin"></i> Updating...</>
                ) : (
                     <><i className="fas fa-save"></i> Update Estimate</>
                )}
             </button>

             <button
                onClick={handleDownload}
                disabled={!isFormValid}
                className={`px-8 py-4 rounded-full font-bold text-lg shadow-2xl flex items-center gap-3 transition-all ${
                    isFormValid 
                    ? "bg-gradient-to-r from-cyan-500 to-blue-500 hover:scale-105 hover:from-cyan-400 hover:to-blue-400 text-white cursor-pointer" 
                    : "bg-gray-600 text-gray-400 opacity-50 cursor-not-allowed"
                }`}
             >
                <i className="fas fa-file-pdf"></i> Download Updated PDF
             </button>
             
             <button
                 onClick={() => navigate("/estimates")} 
                 className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-full font-bold text-lg transition-colors"
            >
                Cancel
            </button>
          </div>

        </div>
        
         <div className="text-center mt-12 text-purple-300/50 text-sm">
           Avantica Pumps Pvt Ltd - Repair Division
         </div>

      </div>

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
             <div className="glass rounded-3xl p-8 max-w-lg w-full text-center border border-emerald-500/30 shadow-2xl relative animate-[fadeIn_0.3s_ease-out]">
                
                 {/* Success Icon */}
                 <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-emerald-500 to-green-400 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/40">
                    <i className="fas fa-check text-4xl text-white"></i>
                 </div>

                 <h2 className="text-3xl font-bold text-white mb-2">Estimate Updated!</h2>
                 <p className="text-purple-200 mb-6">
                    Successfully updated in database.
                    <br />
                    <span className="text-xl font-mono text-emerald-300 mt-2 block">
                        Estimate No: {estimateNo}
                    </span>
                 </p>

                 <div className="flex flex-col gap-3">
                     <button
                        onClick={() => {
                             generateEstimatePDF({ customer, jobDetails, items, total, discountAmount, description, estimateNo, includeGst }, "print");
                        }}
                        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:scale-105 transition-transform"
                     >
                         <i className="fas fa-print"></i> Print Updated Estimate
                     </button>
                     
                      {/* WhatsApp */}
                     <button
                        onClick={() => {
                          const phone = (customer.mobile || "").replace(/\D/g, "");
                          const message = encodeURIComponent(
                            `Hello ${customer.name}, here is your updated repair estimate #${estimateNo}. Total Amount: ₹${total}`
                          );
                          window.open(`https://wa.me/91${phone}?text=${message}`, "_blank");
                        }}
                        className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:scale-105 transition-transform"
                     >
                        <i className="fab fa-whatsapp text-lg"></i> Share on WhatsApp
                     </button>
                     
                     <div className="flex gap-3">
                         <button
                            onClick={() => navigate("/estimates")}
                            className="flex-1 bg-white/10 hover:bg-white/20 text-white py-3 rounded-xl font-bold transition-colors"
                         >
                            Back to List
                         </button>
                         <button
                            onClick={() => setShowSuccessModal(false)}
                            className="flex-1 border border-white/20 text-purple-200 hover:text-white hover:bg-white/5 py-3 rounded-xl font-bold transition-colors"
                         >
                            Keep Editing
                         </button>
                     </div>
                 </div>
             </div>
        </div>
      )}

      <style jsx>{`
        .glass {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(16px);
        }
        .input-glow:focus {
           box-shadow: 0 0 15px rgba(168, 85, 247, 0.4);
        }
        @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default EditEstimate;
